import { FC } from 'react';
import MuiChip from '@mui/material/Chip';
import { Theme } from '@mui/material';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import Icon from '../icon';
import { IconColor, IconType } from '../icon/helpers';
import { getOpaqueSurfaceColors } from '@/lib/theme/opaque-surface-colors';
import { ChipColor, ChipSize } from './helpers';

interface Props {
  icon?: IconType;
  iconColor?: IconColor;
  label: string;
  color?: ChipColor; // @todo mui type for this?
  size?: ChipSize;
  className?: string;
}

const useStyles = createUseStyles((theme: Theme) => {
  const chipSurfaces = getOpaqueSurfaceColors(theme);
  return {
    chip: {
      width: 'fit-content',
      height: 'auto',
      '& .MuiChip-label': {
        padding: `1px 8px`,
        color: theme.palette.chip.label,
      },
      '&.info': {
        backgroundColor: theme.palette.chip.infoBackground,
      },
      '&.error': {
        backgroundColor: chipSurfaces.error,
      },
      '&.warning': {
        backgroundColor: chipSurfaces.warning,
      },
      '&.success': {
        backgroundColor: chipSurfaces.success,
      },
      '&.primary': {
        backgroundColor: theme.palette.chip.primaryBackground,
      },
      '&.secondary': {
        backgroundColor: theme.palette.chip.secondaryBackground,
      },
      '&.grey': {
        backgroundColor: theme.palette.chip.greyBackground,
      },
      '&.critical': {
        backgroundColor: theme.palette.chip.criticalBackground,
        boxShadow: `inset 0 0 0 1px ${theme.palette.chip.criticalOutline}`,
      },
      '&.medium': {
        lineHeight: '24px',
      },
      '&.small': {
        lineHeight: '20px',
      },
      '&.x-small': {
        lineHeight: '16px',
        fontSize: theme.typography.caption.fontSize,
      },
      '& .MuiChip-avatar': {
        fontSize: 'inherit !important',
        width: 'auto',
        height: 'auto',
      },
      '&.withIcon': {
        paddingLeft: 7,
      },
    },
  };
});

const Chip: FC<Props> = ({
  label,
  size = 'medium',
  color = 'default',
  className = '',
  icon,
  iconColor,
}) => {
  const styles = useStyles();
  const compClass = classNames(styles.chip, className, color, size, { withIcon: !!icon });
  const muiSize = size === 'x-small' ? 'small' : size;
  const IconEl = icon ? <Icon size={muiSize} name={icon} color={iconColor} /> : undefined;

  return (
    <MuiChip avatar={IconEl} className={compClass} label={label} size={muiSize} variant="filled" />
  );
};

export default Chip;
